import { API_URL } from '@/config';
import { getPrefetched, getResolvedData, prefetchDetail } from '@/utils/dataPrefetcher';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentGateModal, gateFor } from '@/components/ContentGate';
import { getPreviewSignIds } from '@/utils/freeSigns';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '@/components/BackButton';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Search, AlertTriangle, Ban, Info, Navigation, Lock, UserPlus, Flag, Car, ParkingCircle, CircleDot, ArrowUpDown, Gauge, Route, Home } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import { FAQSchema, TrafficSignsListSchema, PageBreadcrumbs } from '@/components/StructuredData';
import RelatedPages from '@/components/RelatedPages';
import VisibleBreadcrumbs from '@/components/VisibleBreadcrumbs';

import { API_URL as API } from '@/config';

// Translation helper for all 9 languages
// Well-known signs shown first and unlocked for free: Stoppskylt, Ingen omkörning
const FEATURED_FREE_SIGNS = [
  { id: '9f830a97-428e-4688-9577-3667f26768d0', sv: 'Stoppskylt' },
  { id: 'ff938e47-694a-4168-bf77-a432879f486e', sv: 'Ingen omkörning' },
];
const findFeatured = (list) => FEATURED_FREE_SIGNS
  .map(f => list.find(x => x.id === f.id || x.name === f.sv || x.name?.sv === f.sv))
  .filter(Boolean);

const texts = {
  trafficSigns: { en: 'Traffic Signs', ar: 'إشارات المرور', sv: 'Trafikmärken', tr: 'Trafik İşaretleri', fa: 'علائم راهنمایی', so: 'Calaamadaha Taraafikada', pl: 'Znaki Drogowe', es: 'Señales de Tráfico', de: 'Verkehrszeichen' },
  learnSigns: { en: 'Learn the meanings of different traffic signs to pass your driving test', ar: 'تعلم معاني إشارات المرور المختلفة لاجتياز اختبار القيادة بنجاح', sv: 'Lär dig betydelsen av olika trafikmärken för att klara körprovet', tr: 'Sürüş sınavını geçmek için farklı trafik işaretlerinin anlamlarını öğrenin', fa: 'برای قبولی در آزمون رانندگی معانی علائم مختلف راهنمایی را یاد بگیرید', so: 'Baro macnaha calaamadaha taraafikada kala duwan si aad u guuleysato imtixaanka wadista', pl: 'Naucz się znaczeń różnych znaków drogowych, aby zdać egzamin na prawo jazdy', es: 'Aprende los significados de diferentes señales de tráfico para aprobar tu examen de conducir', de: 'Lernen Sie die Bedeutungen verschiedener Verkehrszeichen, um Ihre Fahrprüfung zu bestehen' },
  subscriptionRequiredSigns: { en: 'Create a free account or subscribe to unlock more traffic signs', ar: 'أنشئ حساباً مجانياً أو اشترك لفتح المزيد من إشارات المرور', sv: 'Skapa ett gratis konto eller prenumerera för att låsa upp fler trafikmärken', tr: 'Daha fazla trafik işaretini açmak için ücretsiz hesap oluşturun veya abone olun', fa: 'برای باز کردن علائم بیشتر یک حساب رایگان بسازید یا مشترک شوید', so: 'Samee akoon bilaash ah ama is diiwaangeli si aad u furto calaamado badan', pl: 'Załóż darmowe konto lub subskrybuj, aby odblokować więcej znaków', es: 'Crea una cuenta gratuita o suscríbete para desbloquear más señales', de: 'Erstelle ein kostenloses Konto oder abonniere, um mehr Verkehrszeichen freizuschalten' },
  search: { en: 'Search...', ar: 'ابحث...', sv: 'Sök...', tr: 'Ara...', fa: 'جستجو...', so: 'Raadi...', pl: 'Szukaj...', es: 'Buscar...', de: 'Suchen...' },
  loading: { en: 'Loading...', ar: 'جاري التحميل...', sv: 'Laddar...', tr: 'Yükleniyor...', fa: 'در حال بارگذاری...', so: 'Soo dejinaya...', pl: 'Ładowanie...', es: 'Cargando...', de: 'Laden...' },
  noSignsFound: { en: 'No signs found', ar: 'لا توجد إشارات', sv: 'Inga skyltar hittades', tr: 'İşaret bulunamadı', fa: 'علامتی یافت نشد', so: 'Calaamad lama helin', pl: 'Nie znaleziono znaków', es: 'No se encontraron señales', de: 'Keine Zeichen gefunden' },
  scrollDown: { en: 'Scroll down for more', ar: 'مرر للأسفل لمشاهدة المزيد', sv: 'Scrolla ner för mer', tr: 'Daha fazlası için aşağı kaydırın', fa: 'برای بیشتر به پایین بروید', so: 'Hoos u dhaadhac si aad u aragto wax badan', pl: 'Przewiń w dół, aby zobaczyć więcej', es: 'Desplázate hacia abajo para más', de: 'Nach unten scrollen für mehr' },
  total: { en: 'Total', ar: 'إجمالي', sv: 'Totalt', tr: 'Toplam', fa: 'مجموع', so: 'Wadarta', pl: 'Łącznie', es: 'Total', de: 'Gesamt' },
  signs: { en: 'signs', ar: 'إشارة', sv: 'skyltar', tr: 'işaret', fa: 'علامت', so: 'calaamadood', pl: 'znaków', es: 'señales', de: 'Zeichen' },
  signsForSubscribers: { en: 'Traffic Signs for Subscribers', ar: 'إشارات المرور للمشتركين', sv: 'Trafikmärken för prenumeranter', tr: 'Aboneler için Trafik İşaretleri', fa: 'علائم راهنمایی برای مشترکین', so: 'Calaamadaha Taraafikada ee qoraga', pl: 'Znaki Drogowe dla Subskrybentów', es: 'Señales de Tráfico para Suscriptores', de: 'Verkehrszeichen für Abonnenten' },
  subscribeToLearn: { en: 'Subscribe now to learn all traffic signs!', ar: 'اشترك الآن لتعلم جميع إشارات المرور!', sv: 'Prenumerera nu för att lära dig alla trafikmärken!', tr: 'Tüm trafik işaretlerini öğrenmek için şimdi abone olun!', fa: 'برای یادگیری همه علائم راهنمایی اشتراک بگیرید!', so: 'Hadda iska qor si aad u barato dhammaan calaamadaha taraafikada!', pl: 'Subskrybuj, aby nauczyć się wszystkich znaków drogowych!', es: '¡Suscríbete ahora para aprender todas las señales de tráfico!', de: 'Jetzt abonnieren, um alle Verkehrszeichen zu lernen!' },
  subscribeNow: { en: 'Subscribe Now', ar: 'اشترك الآن', sv: 'Prenumerera nu', tr: 'Şimdi Abone Ol', fa: 'اشتراک', so: 'Hadda Iska Qor', pl: 'Subskrybuj Teraz', es: 'Suscríbete Ahora', de: 'Jetzt Abonnieren' },
  cancel: { en: 'Cancel', ar: 'إلغاء', sv: 'Avbryt', tr: 'İptal', fa: 'لغو', so: 'Jooji', pl: 'Anuluj', es: 'Cancelar', de: 'Abbrechen' },
  errorLoading: { en: 'Error loading signs', ar: 'خطأ في تحميل الإشارات', sv: 'Fel vid laddning av skyltar', tr: 'İşaretler yüklenirken hata', fa: 'خطا در بارگذاری علائم', so: 'Khalad ku dhacay soo dejinta calaamadaha', pl: 'Błąd ładowania znaków', es: 'Error al cargar las señales', de: 'Fehler beim Laden der Zeichen' },
  reportError: { en: 'Report Error', ar: 'الإبلاغ عن خطأ', sv: 'Rapportera fel', tr: 'Hata Bildir', fa: 'گزارش خطا', so: 'Ka warbixin khalad', pl: 'Zgłoś błąd', es: 'Reportar Error', de: 'Fehler melden' },
  reportTitle: { en: 'Report an Error', ar: 'الإبلاغ عن خطأ', sv: 'Rapportera ett fel', tr: 'Bir Hata Bildir', fa: 'گزارش یک خطا', so: 'Ka warbixin khalad', pl: 'Zgłoś błąd', es: 'Reportar un error', de: 'Einen Fehler melden' },
  reportType: { en: 'Report Type', ar: 'نوع البلاغ', sv: 'Typ av rapport', tr: 'Rapor Türü', fa: 'نوع گزارش', so: 'Nooca warbixinta', pl: 'Typ raportu', es: 'Tipo de reporte', de: 'Berichttyp' },
  incorrectInfo: { en: 'Incorrect Information', ar: 'معلومات غير صحيحة', sv: 'Felaktig information', tr: 'Yanlış Bilgi', fa: 'اطلاعات نادرست', so: 'Macluumaad khaldan', pl: 'Nieprawidłowe informacje', es: 'Información incorrecta', de: 'Falsche Information' },
  imageIssue: { en: 'Image Issue', ar: 'مشكلة في الصورة', sv: 'Bildproblem', tr: 'Resim Sorunu', fa: 'مشکل تصویر', so: 'Dhibaato sawir', pl: 'Problem z obrazem', es: 'Problema de imagen', de: 'Bildproblem' },
  translationError: { en: 'Translation Error', ar: 'خطأ في الترجمة', sv: 'Översättningsfel', tr: 'Çeviri Hatası', fa: 'خطای ترجمه', so: 'Khalad turjumaad', pl: 'Błąd tłumaczenia', es: 'Error de traducción', de: 'Übersetzungsfehler' },
  otherIssue: { en: 'Other Issue', ar: 'مشكلة أخرى', sv: 'Annat problem', tr: 'Diğer Sorun', fa: 'مشکل دیگر', so: 'Dhibaato kale', pl: 'Inny problem', es: 'Otro problema', de: 'Anderes Problem' },
  details: { en: 'Details', ar: 'التفاصيل', sv: 'Detaljer', tr: 'Detaylar', fa: 'جزئیات', so: 'Faahfaahin', pl: 'Szczegóły', es: 'Detalles', de: 'Details' },
  describeIssue: { en: 'Please describe the issue...', ar: 'يرجى وصف المشكلة...', sv: 'Beskriv problemet...', tr: 'Lütfen sorunu açıklayın...', fa: 'لطفاً مشکل را شرح دهید...', so: 'Fadlan sharax dhibaatada...', pl: 'Proszę opisać problem...', es: 'Por favor describa el problema...', de: 'Bitte beschreiben Sie das Problem...' },
  submit: { en: 'Submit', ar: 'إرسال', sv: 'Skicka', tr: 'Gönder', fa: 'ارسال', so: 'Dir', pl: 'Wyślij', es: 'Enviar', de: 'Absenden' },
  reportSuccess: { en: 'Report submitted successfully!', ar: 'تم إرسال البلاغ بنجاح!', sv: 'Rapporten har skickats!', tr: 'Rapor başarıyla gönderildi!', fa: 'گزارش با موفقیت ارسال شد!', so: 'Warbixinta si guul leh ayaa loo diray!', pl: 'Raport wysłany pomyślnie!', es: '¡Reporte enviado con éxito!', de: 'Bericht erfolgreich gesendet!' },
  reportFailed: { en: 'Failed to submit report', ar: 'فشل في إرسال البلاغ', sv: 'Det gick inte att skicka rapporten', tr: 'Rapor gönderilemedi', fa: 'ارسال گزارش ناموفق بود', so: 'Waa lagu guul daraystay dirista warbixinta', pl: 'Nie udało się wysłać raportu', es: 'Error al enviar el reporte', de: 'Bericht konnte nicht gesendet werden' },
  allSigns: { en: 'All Signs', ar: 'جميع الإشارات', sv: 'Alla skyltar', tr: 'Tüm İşaretler', fa: 'همه علائم', so: 'Dhammaan Calaamadaha', pl: 'Wszystkie Znaki', es: 'Todas las Señales', de: 'Alle Zeichen' },
  warning: { en: 'Warning', ar: 'تحذيرية', sv: 'Varning', tr: 'Uyarı', fa: 'هشدار', so: 'Digniin', pl: 'Ostrzeżenie', es: 'Advertencia', de: 'Warnung' },
  prohibition: { en: 'Prohibition', ar: 'منع', sv: 'Förbud', tr: 'Yasak', fa: 'ممنوعیت', so: 'Mamnuuc', pl: 'Zakaz', es: 'Prohibición', de: 'Verbot' },
  mandatory: { en: 'Mandatory', ar: 'إلزامية', sv: 'Obligatorisk', tr: 'Zorunlu', fa: 'اجباری', so: 'Waajibaad', pl: 'Obowiązkowe', es: 'Obligatorio', de: 'Pflicht' },
  informative: { en: 'Informative', ar: 'إرشادية', sv: 'Informativ', tr: 'Bilgilendirici', fa: 'اطلاعاتی', so: 'Macluumaadeed', pl: 'Informacyjne', es: 'Informativo', de: 'Informativ' },
  pedestrian_cycling: { en: 'Pedestrian & Cycling', ar: 'مشاة ودراجات', sv: 'Gång & Cykel', tr: 'Yaya ve Bisiklet', fa: 'پیاده و دوچرخه', so: 'Lugeyn & Baaskiil', pl: 'Piesi i Rowerzyści', es: 'Peatones y Ciclistas', de: 'Fußgänger & Radfahrer' },
  parking: { en: 'Parking', ar: 'مواقف السيارات', sv: 'Parkering', tr: 'Park Yeri', fa: 'پارکینگ', so: 'Joojin', pl: 'Parkowanie', es: 'Estacionamiento', de: 'Parken' },
  priority: { en: 'Priority', ar: 'أولوية المرور', sv: 'Prioritet', tr: 'Öncelik', fa: 'حق تقدم', so: 'Mudnaanta', pl: 'Pierwszeństwo', es: 'Prioridad', de: 'Vorfahrt' },
  speed: { en: 'Speed', ar: 'السرعة', sv: 'Hastighet', tr: 'Hız', fa: 'سرعت', so: 'Xawaare', pl: 'Prędkość', es: 'Velocidad', de: 'Geschwindigkeit' },
  highway: { en: 'Highway', ar: 'الطرق السريعة', sv: 'Motorväg', tr: 'Otoyol', fa: 'بزرگراه', so: 'Waddada Weyn', pl: 'Autostrada', es: 'Autopista', de: 'Autobahn' },
  residential: { en: 'Residential', ar: 'مناطق سكنية', sv: 'Bostadsområde', tr: 'Konut Alanı', fa: 'منطقه مسکونی', so: 'Degaan', pl: 'Obszar Mieszkalny', es: 'Zona Residencial', de: 'Wohngebiet' },
  retry: { en: 'Retry', ar: 'إعادة المحاولة', sv: 'Försök igen', tr: 'Tekrar dene', fa: 'تلاش مجدد', so: 'Isku day mar kale', pl: 'Ponów', es: 'Reintentar', de: 'Erneut versuchen' },
};

const getText = (key, lang) => texts[key]?.[lang] || texts[key]?.en || key;

const TrafficSignsPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [signs, setSigns] = useState(() => getResolvedData('signs') || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(() => !getResolvedData('signs'));
  const [fetchError, setFetchError] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportingSign, setReportingSign] = useState(null);
  const [reportType, setReportType] = useState('incorrect_info');
  const [reportDetails, setReportDetails] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const [freeSignIds, setFreeSignIds] = useState([]);
  const lang = i18n.language;
  const isRTL = lang === 'ar' || lang === 'fa';
  
  // التحقق إذا كان المستخدم مشترك
  const isSubscribed = user?.subscription_status === 'active' || user?.taxi_subscription_status === 'active' || user?.is_admin || user?.role === 'super_admin';

  const categories = [
    { value: 'all', labelKey: 'allSigns', icon: Navigation },
    { value: 'warning', labelKey: 'warning', icon: AlertTriangle },
    { value: 'prohibition', labelKey: 'prohibition', icon: Ban },
    { value: 'mandatory', labelKey: 'mandatory', icon: CircleDot },
    { value: 'informative', labelKey: 'informative', icon: Info },
    { value: 'pedestrian_cycling', labelKey: 'pedestrian_cycling', icon: Car },
    { value: 'parking', labelKey: 'parking', icon: ParkingCircle },
    { value: 'priority', labelKey: 'priority', icon: ArrowUpDown },
    { value: 'speed', labelKey: 'speed', icon: Gauge },
    { value: 'highway', labelKey: 'highway', icon: Route },
    { value: 'residential', labelKey: 'residential', icon: Home }
  ];

  useEffect(() => {
    fetchSigns();
  }, [i18n.language]);

  // تحميل تفاصيل أول إشارتين مجانيتين تلقائياً
  useEffect(() => {
    if (signs.length > 0) {
      signs.slice(0, 2).forEach(sign => {
        prefetchDetail('sign', sign.slug || sign.id);
      });
    }
  }, [signs]);

  const fetchSigns = async () => {
    setFetchError(false);
    try {
      // Only use prefetch for Swedish (default prefetch language)
      if (i18n.language === 'sv') {
        const prefetched = await getPrefetched('signs');
        if (prefetched) {
          setSigns(prefetched);
          setLoading(false);
          return;
        }
      }
      setLoading(true);
      const response = await axios.get(`${API}/api/traffic-signs?lang=${i18n.language || 'sv'}`);
      setSigns(response.data || []);
    } catch (error) {
      console.error('Error fetching traffic signs:', error);
      setFetchError(true);
      toast.error(getText('errorLoading', lang));
    } finally {
      setLoading(false);
    }
  };

  const filteredSigns = useMemo(() => {
    if (signs.length === 0) return [];
    
    let filtered = [...signs];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(sign => sign.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(sign => {
        const name = typeof sign.name === 'string' ? sign.name : (sign.name?.[i18n.language] || sign.name?.en || '');
        const description = sign.description?.[i18n.language] || sign.description?.en || '';
        return name.toLowerCase().includes(query) || description.toLowerCase().includes(query);
      });
    }

    if (selectedCategory !== 'all') {
      const categoryPriority = {
        'warning': 1,
        'prohibition': 2,
        'mandatory': 3,
        'priority': 4,
        'speed': 5,
        'highway': 6,
        'residential': 7,
        'pedestrian_cycling': 8,
        'parking': 9,
        'informative': 10
      };

      filtered.sort((a, b) => {
        const priorityA = categoryPriority[a.category] || 99;
        const priorityB = categoryPriority[b.category] || 99;
        return priorityA - priorityB;
      });
    } else {
      // Keep the DISPLAY order in sync with the free-sign order below so
      // the two unlocked tiles always appear as the first two in the grid.
      const categoryOrder = ['priority', 'prohibition', 'warning', 'mandatory', 'speed', 'highway', 'residential', 'pedestrian_cycling', 'parking', 'informative'];
      const byCategory = {};
      
      categoryOrder.forEach(cat => {
        byCategory[cat] = filtered.filter(s => s.category === cat);
      });
      
      const mixed = [];
      let hasMore = true;
      let index = 0;
      
      while (hasMore) {
        hasMore = false;
        for (const cat of categoryOrder) {
          if (byCategory[cat] && byCategory[cat][index]) {
            mixed.push(byCategory[cat][index]);
            hasMore = true;
          }
        }
        index++;
      }
      
      const featured = findFeatured(mixed);
      filtered = [...featured, ...mixed.filter(x => !featured.includes(x))];
    }

    return filtered;
  }, [signs, searchQuery, selectedCategory, i18n.language]);
  
  // تحديد الإشارات المجانية الثابتة (أول إشارتين بعد الخلط)
  useEffect(() => {
    if (signs.length > 0 && freeSignIds.length === 0) {
      setFreeSignIds(getPreviewSignIds(signs));
    }
  }, [signs]);

  const getCategoryColor = (category) => {
    const colors = {
      warning: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      prohibition: 'bg-red-100 text-red-800 border-red-300',
      mandatory: 'bg-blue-100 text-blue-800 border-blue-300',
      informative: 'bg-green-100 text-green-800 border-green-300',
      pedestrian_cycling: 'bg-purple-100 text-purple-800 border-purple-300',
      parking: 'bg-indigo-100 text-indigo-800 border-indigo-300',
      priority: 'bg-orange-100 text-orange-800 border-orange-300',
      speed: 'bg-pink-100 text-pink-800 border-pink-300',
      highway: 'bg-cyan-100 text-cyan-800 border-cyan-300',
      residential: 'bg-teal-100 text-teal-800 border-teal-300',
      general: 'bg-slate-100 text-slate-800 border-slate-300'
    };
    return colors[category] || colors.general;
  };

  // Generate slug from Swedish name
  const generateSlug = (name) => {
    if (!name) return '';
    const sv = typeof name === 'string' ? name : (name.sv || name.en || '');
    return sv.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  };

  // Get localized sign detail path
  const getSignPath = (sign) => {
    const slug = generateSlug(sign.name);
    const langPaths = {
      sv: '/trafikmarken',
      ar: '/ar/isharat-almroor',
      en: '/en/traffic-signs',
      tr: '/tr/trafik-isaretleri',
      fa: '/fa/alamat-rahnamaii',
      so: '/so/calaamadaha-taraafikada',
      de: '/de/verkehrszeichen',
      es: '/es/senales-trafico',
      pl: '/pl/znaki-drogowe',
    };
    const basePath = langPaths[i18n.language] || langPaths.sv;
    return `${basePath}/${slug || sign.id}`;
  };

  // دالة للتعامل مع النقر على إشارة المرور
  const handleSignClick = (sign, gate = 'plans') => {
    if (isSubscribed || gate === 'open') {
      navigate(getSignPath(sign), { state: { signData: sign } });
    } else {
      setShowSubscribeModal(gate);
    }
  };

  // دالة للإبلاغ عن خطأ
  const handleReportClick = (e, sign) => {
    e.stopPropagation();
    setReportingSign(sign);
    setReportType('incorrect_info');
    setReportDetails('');
    setShowReportModal(true);
  };

  // دالة لإرسال البلاغ
  const handleSubmitReport = async () => {
    if (!reportDetails.trim()) return;
    
    setSubmittingReport(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/site-reports`,
        {
          url: `/vagmarken`,
          report_type: reportType,
          details: `[Traffic Sign: ${reportingSign?.name?.en || reportingSign?.id}] ${reportDetails}`,
          item_id: reportingSign?.id,
          item_type: 'traffic_sign'
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      toast.success(getText('reportSuccess', lang));
      setShowReportModal(false);
      setReportingSign(null);
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error(getText('reportFailed', lang));
    } finally {
      setSubmittingReport(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-950" dir={isRTL ? 'rtl' : 'ltr'}>
      <SEO page="traffic-signs" />
      <FAQSchema language={i18n.language} page="traffic-signs" />
      <TrafficSignsListSchema language={i18n.language} signs={signs} />
      <PageBreadcrumbs page="traffic-signs" language={i18n.language} />
      <Navbar />
      <VisibleBreadcrumbs />
      
      <main className="flex-grow container mx-auto px-4 pt-3 pb-8">
        <BackButton to="/" className="mb-4" />
        
        {/* رسالة للمستخدم غير المشترك */}
        {!isSubscribed && (
          <div className="mb-6 inline-flex items-center gap-2 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 px-4 py-2 rounded-full text-sm border border-transparent dark:border-amber-800/30">
            <Lock className="w-4 h-4" />
            {getText('subscriptionRequiredSigns', lang)}
          </div>
        )}
        
        {/* Header - Compact on mobile */}
        <div className="text-center mb-6 sm:mb-10">
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-2 sm:mb-4">
            🚦 {getText('trafficSigns', lang)}
          </h1>
          <p className="text-sm sm:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto hidden sm:block">
            {getText('learnSigns', lang)}
          </p>
        </div>

        {/* Search and Filter - Compact on mobile */}
        <div className="mb-4 sm:mb-8 space-y-3 sm:space-y-4">
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 ${isRTL ? 'right-3' : 'left-3'}`} />
            <Input
              type="text"
              placeholder={getText('search', lang)}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full ${isRTL ? 'pr-10' : 'pl-10'} py-2 sm:py-3 text-sm sm:text-lg border-2 focus:border-emerald-500 rounded-xl`}
            />
          </div>

          {/* Category Filter - Scrollable on mobile */}
          <div className="flex gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:justify-center sm:overflow-visible scrollbar-hide">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = selectedCategory === cat.value;
              return (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-medium transition-all whitespace-nowrap text-xs sm:text-sm flex-shrink-0 ${
                    isActive
                      ? 'bg-emerald-500 text-white shadow-lg'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-600'
                  }`}
                >
                  <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{getText(cat.labelKey, lang)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Results Count - Smaller */}
        <div className="text-center mb-3 sm:mb-6">
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            {filteredSigns.length} / {signs.length}
          </p>
        </div>

        {/* Signs Grid */}
        {loading && signs.length === 0 ? (
          null
        ) : fetchError && signs.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-red-500 mb-4">
              {getText('errorLoading', lang)}
            </p>
            <button 
              onClick={fetchSigns}
              className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
              data-testid="retry-load-signs"
            >
              {getText('retry', lang)}
            </button>
          </div>
        ) : filteredSigns.length === 0 && signs.length > 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-slate-500">
              {getText('noSignsFound', lang)}
            </p>
          </div>
        ) : (
          <>
            {/* Scroll indicator for mobile */}
            <div className="flex items-center justify-center gap-2 mb-4 text-slate-500 text-sm animate-bounce sm:hidden">
              <span>👇</span>
              <span>{getText('scrollDown', lang)}</span>
              <span>👇</span>
            </div>
            
            {/* Compact Grid - More items visible */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2 sm:gap-3">
              {filteredSigns.map((sign, index) => {
                const rawUrl = sign.image_url?.startsWith('http') 
                  ? sign.image_url 
                  : sign.image_url?.startsWith('/api/uploads/') ? sign.image_url : `${API_URL}${sign.image_url}`;
                const imageUrl = rawUrl + (rawUrl.includes('?') ? '&' : '?') + 'v=2';
                // الإشارتين المجانيتين ثابتتين
                const freeIdx = selectedCategory === 'all' ? freeSignIds.indexOf(sign.id) : -1;
                const gate = gateFor(freeIdx === -1 ? 99 : freeIdx, user, isSubscribed);
                const showLock = gate !== 'open';
                
                return (
                  <div 
                    key={sign.id}
                    className="group bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl border-2 border-slate-200 dark:border-slate-700 hover:border-emerald-400 dark:hover:border-emerald-600 hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden relative"
                    onClick={() => handleSignClick(sign, gate)}
                    onMouseEnter={() => {
                      const slug = generateSlug(sign.name);
                      prefetchDetail('sign', slug || sign.id);
                    }}
                    data-testid={`sign-card-${sign.id}`}
                  >
                    {/* Lock overlay for non-subscribers (except first 2) */}
                    {showLock && (
                      <div className="absolute inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-[1px] z-10 flex items-center justify-center cursor-pointer" onClick={(e) => { e.stopPropagation(); setShowSubscribeModal(gate); }} data-testid="content-lock-overlay">
                        <div className="bg-white/90 dark:bg-slate-700/90 rounded-full p-1.5 sm:p-2">
                          {gate === 'register' ? <UserPlus className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 dark:text-blue-300" /> : <Lock className="w-3 h-3 sm:w-4 sm:h-4 text-slate-700 dark:text-slate-300" />}
                        </div>
                      </div>
                    )}
                    
                    {/* Report Button - في الأسفل للجوال، في الأعلى للشاشات الكبيرة */}
                    <button
                      onClick={(e) => handleReportClick(e, sign)}
                      className={`absolute bottom-1 sm:top-1 sm:bottom-auto ${isRTL ? 'right-1' : 'left-1'} z-20 p-1 sm:p-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md`}
                      title={getText('reportError', lang)}
                      data-testid={`report-sign-${sign.id}`}
                    >
                      <Flag className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    </button>
                    
                    {/* Compact Image */}
                    <div className="relative aspect-square bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 p-2 sm:p-3">
                      <img
                        src={imageUrl}
                        alt={typeof sign.name === 'string' ? sign.name : (sign.name?.[i18n.language] || sign.name?.en)}
                        loading={index < 21 ? "eager" : "lazy"}
                        decoding={index < 21 ? "sync" : "async"}
                        fetchPriority={index < 7 ? "high" : "auto"}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-200"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f1f5f9" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="60"%3E🚦%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      {/* Small Category indicator */}
                      <div className={`absolute top-1 ${isRTL ? 'left-1' : 'right-1'} w-2 h-2 sm:w-3 sm:h-3 rounded-full ${
                        sign.category === 'warning' ? 'bg-yellow-500' :
                        sign.category === 'prohibition' ? 'bg-red-500' :
                        sign.category === 'mandatory' ? 'bg-blue-500' :
                        sign.category === 'informative' ? 'bg-green-500' :
                        sign.category === 'pedestrian_cycling' ? 'bg-purple-500' :
                        sign.category === 'parking' ? 'bg-indigo-500' :
                        sign.category === 'priority' ? 'bg-orange-500' :
                        sign.category === 'speed' ? 'bg-pink-500' :
                        sign.category === 'highway' ? 'bg-cyan-500' :
                        sign.category === 'residential' ? 'bg-teal-500' : 'bg-slate-400'
                      }`}></div>
                    </div>

                    {/* Compact Name */}
                    <div className="px-2 py-1.5 sm:px-3 sm:py-2 border-t border-slate-100 dark:border-slate-700">
                      <h3 className="font-medium text-[10px] sm:text-xs text-slate-800 dark:text-slate-200 line-clamp-2 text-center leading-tight">
                        {typeof sign.name === 'string' ? sign.name : (sign.name?.[i18n.language] || sign.name?.en)}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Bottom indicator showing more content */}
            {filteredSigns.length > 12 && (
              <div className="text-center mt-6 text-slate-500 text-sm">
                <p>{getText('total', lang)}: {filteredSigns.length} {getText('signs', lang)}</p>
              </div>
            )}
          </>
        )}
      </main>

      <div className="max-w-6xl mx-auto px-4 sm:px-6"><RelatedPages currentPage="signs" /></div>
      <Footer />

      {/* مودال الاشتراك */}
      <ContentGateModal mode={showSubscribeModal || null} onClose={() => setShowSubscribeModal(false)} lang={lang} />

      {/* مودال الإبلاغ عن خطأ */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 relative" dir={isRTL ? 'rtl' : 'ltr'}>
            <button 
              onClick={() => setShowReportModal(false)}
              className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors`}
            >
              ✕
            </button>
            
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-950/30 rounded-full flex items-center justify-center">
                <Flag className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{getText('reportTitle', lang)}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">{reportingSign?.name?.[lang] || reportingSign?.name?.en}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {getText('reportType', lang)}
                </label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                >
                  <option value="incorrect_info">{getText('incorrectInfo', lang)}</option>
                  <option value="image_issue">{getText('imageIssue', lang)}</option>
                  <option value="translation_error">{getText('translationError', lang)}</option>
                  <option value="other">{getText('otherIssue', lang)}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {getText('details', lang)}
                </label>
                <textarea
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  placeholder={getText('describeIssue', lang)}
                  rows={4}
                  className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 py-3 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  {getText('cancel', lang)}
                </button>
                <button
                  onClick={handleSubmitReport}
                  disabled={!reportDetails.trim() || submittingReport}
                  className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 dark:disabled:bg-slate-600 text-white font-semibold rounded-xl transition-colors"
                >
                  {submittingReport ? '...' : getText('submit', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrafficSignsPage;
